import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Blocker, Location, useBlocker } from 'react-router'

export type NavigationBlockerOnReleased = (navigation: {
  currentLocation: Location
  nextLocation: Location
}) => 'handled' | void

type HandlerRegistration = {
  id: string
  /** Higher numbers run first. Defaults to 0. Ties keep registration order. */
  priority?: number
  shouldBlock: boolean
  onReleased?: NavigationBlockerOnReleased
}

type PendingNavigation = {
  currentLocation: Location
  nextLocation: Location
}

/**
 * `blocked` — a handler is holding the navigation and owes an answer.
 * `handled` — a handler took the navigation itself, so the router must not.
 * `released` — everybody had a turn and nobody wanted it.
 */
type ProcessHandlersResult = 'blocked' | 'handled' | 'released'

type NavigationBlockerContextValue = {
  registerHandler: (registration: HandlerRegistration) => void
  unregisterHandler: (id: string) => void
  proceed: () => void
  reset: () => void
  isActiveHandler: (id: string) => boolean
  blocker: Blocker
}

const NavigationBlockerContext = createContext<NavigationBlockerContextValue | undefined>(undefined)

const sortHandlers = (handlers: HandlerRegistration[]) =>
  [...handlers].sort((left, right) => (right.priority ?? 0) - (left.priority ?? 0))

export const useNavigationBlocker = () => {
  const context = useContext(NavigationBlockerContext)
  if (context === undefined) {
    throw new Error('useNavigationBlocker must be used within NavigationBlockerProvider')
  }
  return context
}

export const useRegisterNavigationBlocker = ({
  id,
  priority = 0,
  shouldBlock,
  onReleased,
}: HandlerRegistration) => {
  const { registerHandler, unregisterHandler } = useNavigationBlocker()
  const onReleasedRef = useRef(onReleased)
  onReleasedRef.current = onReleased

  useEffect(() => {
    registerHandler({
      id,
      priority,
      shouldBlock,
      onReleased: navigation => onReleasedRef.current?.(navigation),
    })
  }, [id, priority, shouldBlock, registerHandler])

  useEffect(() => () => unregisterHandler(id), [id, unregisterHandler])
}

export const NavigationBlockerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const handlersRef = useRef<HandlerRegistration[]>([])
  const seenIdsRef = useRef<Set<string>>(new Set())
  /** Set only while a navigation is held up and still owed an answer. */
  const pendingNavigationRef = useRef<PendingNavigation | null>(null)
  const [activeHandlerId, setActiveHandlerId] = useState<string | null>(null)
  const activeHandlerIdRef = useRef<string | null>(null)

  const setActive = useCallback((id: string | null) => {
    activeHandlerIdRef.current = id
    setActiveHandlerId(id)
  }, [])

  /**
   * Gives every remaining handler a turn, highest priority first: one that wants
   * to block becomes the active handler and stops here, everyone else gets
   * `onReleased`.
   */
  const processHandlers = useCallback(
    (navigation: PendingNavigation): ProcessHandlersResult => {
      for (const handler of sortHandlers(handlersRef.current)) {
        if (seenIdsRef.current.has(handler.id)) {
          continue
        }
        seenIdsRef.current.add(handler.id)

        if (handler.shouldBlock) {
          setActive(handler.id)
          return 'blocked'
        }

        if (handler.onReleased?.(navigation) === 'handled') {
          setActive(null)
          return 'handled'
        }
      }

      setActive(null)
      return 'released'
    },
    [setActive],
  )

  const shouldBlockNavigation = useCallback(
    (navigation: PendingNavigation) => {
      seenIdsRef.current = new Set()
      pendingNavigationRef.current = navigation
      const result = processHandlers(navigation)
      if (result !== 'blocked') {
        pendingNavigationRef.current = null
      }
      return result !== 'released'
    },
    [processHandlers],
  )

  const blocker = useBlocker(shouldBlockNavigation)
  const blockerRef = useRef(blocker)
  blockerRef.current = blocker

  const proceed = useCallback(() => {
    const navigation = pendingNavigationRef.current
    if (!navigation || blockerRef.current.state !== 'blocked') {
      return
    }

    const result = processHandlers(navigation)
    if (result === 'blocked') {
      return
    }

    pendingNavigationRef.current = null
    if (result === 'released') {
      seenIdsRef.current = new Set()
      blockerRef.current.proceed()
    }
  }, [processHandlers])

  const reset = useCallback(() => {
    seenIdsRef.current = new Set()
    pendingNavigationRef.current = null
    setActive(null)
    if (blockerRef.current.state === 'blocked') {
      blockerRef.current.reset()
    }
  }, [setActive])

  const registerHandler = useCallback((registration: HandlerRegistration) => {
    const exists = handlersRef.current.some(handler => handler.id === registration.id)
    handlersRef.current = exists
      ? handlersRef.current.map(handler =>
          handler.id === registration.id ? registration : handler,
        )
      : [...handlersRef.current, registration]
  }, [])

  const unregisterHandler = useCallback(
    (id: string) => {
      handlersRef.current = handlersRef.current.filter(handler => handler.id !== id)
      if (activeHandlerIdRef.current === id) {
        setActive(null)
      }
    },
    [setActive],
  )

  // The handler holding the navigation has gone (its route or modal unmounted),
  // so hand the turn on rather than leaving the router blocked with nobody left
  // to release it. An effect rather than a call from `unregisterHandler`: on
  // teardown React runs cleanups without ever re-running effects, so this stays
  // quiet while the whole tree is unmounting.
  useEffect(() => {
    if (blocker.state === 'blocked' && activeHandlerId === null && pendingNavigationRef.current) {
      proceed()
    }
  }, [activeHandlerId, blocker.state, proceed])

  const isActiveHandler = useCallback((id: string) => activeHandlerId === id, [activeHandlerId])

  const contextValue = useMemo(
    () => ({
      registerHandler,
      unregisterHandler,
      proceed,
      reset,
      isActiveHandler,
      blocker,
    }),
    [blocker, isActiveHandler, proceed, registerHandler, reset, unregisterHandler],
  )

  return (
    <NavigationBlockerContext.Provider value={contextValue}>
      {children}
    </NavigationBlockerContext.Provider>
  )
}
